import { Component, DestroyRef, inject, computed } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ShowDeviceFacade } from '../../../../../Facades/Intune/show-device.facade';
import { MsgService } from '../../../../../Modules/Shared/Services/msg.service';
import { DeviceActionDialogComponent } from '../../dialogs/device-action-dialog/device-action-dialog.component';
import { BitlockerKeysDialogComponent } from '../../dialogs/bitlocker-keys-dialog/bitlocker-keys-dialog.component';
import {
  DeviceActionDialogData,
  DeviceActionDialogAction,
} from '../../../Interfaces/device-action-dialog.interface';
import {
  WipePayload,
  FreshStartPayload,
} from '../../../../../Interfaces/api/Intune/devices.interface';
import { Observable, filter, switchMap } from 'rxjs';
import { BitlockerKeyItem } from '../../../Interfaces/bitlocker-key.interface';
import { MatTooltip } from "@angular/material/tooltip";
import { AuthService } from '../../../../Shared/Services/auth.service';
import { PERMISSIONS } from '../../../../../Enums/permission.enum';
import { ACTION_KEYS } from '../../../Consts/device-action-keys.const';

@Component({
  selector: 'app-device-actions',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, TranslateModule, MatTooltip],
  templateUrl: './device-actions.component.html',
  styleUrls: ['./device-actions.component.scss'],
})
export class DeviceActionsComponent {
  private readonly _showDeviceFacade = inject(ShowDeviceFacade);
  private readonly _dialog = inject(MatDialog);
  private readonly _msg = inject(MsgService);
  private readonly _translate = inject(TranslateService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _authService = inject(AuthService);

  readonly permissions = PERMISSIONS;

  readonly currentUser = toSignal(this._authService.currentUser$, { initialValue: null });

  readonly device = this._showDeviceFacade.device;

  private readonly _actionDefs: { permission: PERMISSIONS; label: string; icon: string; action: () => void; class?: string }[] = [
    { permission: PERMISSIONS.DEVICE_LOCAL_CREDENTIAL_RESET, label: `${ACTION_KEYS.lock_credentials}.label`, icon: 'key', action: () => this.lockCredentials(), class: 'icon-lock-credentials' },
    { permission: PERMISSIONS.DEVICE_AUTOPILOT_RESET, label: `${ACTION_KEYS.autopilot_reset}.label`, icon: 'power_settings_new', action: () => this.autopilotReset(), class: 'icon-autopilot-reset' },
    { permission: PERMISSIONS.DEVICE_RETRIEVE_BITLOCKER, label: `${ACTION_KEYS.retrieve_bitlocker}.label`, icon: 'visibility', action: () => this.retrieveBitlocker(), class: 'icon-retrieve-bitlocker' },
    { permission: PERMISSIONS.DEVICE_WIPE, label: `${ACTION_KEYS.wipe}.label`, icon: 'cleaning_services', action: () => this.wipe(), class: 'icon-wipe' },
    { permission: PERMISSIONS.DEVICE_FRESH_START, label: `${ACTION_KEYS.fresh_start}.label`, icon: 'restart_alt', action: () => this.freshStart(), class: 'icon-fresh-start' },
  ];

  readonly visibleActions = computed(() => {
    const accessibility = this.device()?.actions_accessibility;
    return this._actionDefs.filter(a =>
      this.currentUser()?.hasPermission(a.permission) &&
      accessibility?.find(x => x.permission_name === a.permission)?.accessible !== false
    );
  });

  readonly agentInfo = computed(() => {
    const d = this.device();
    if (!d?.agent) return { state: 'inactive', messageKey: 'app.device.agent.inactive', lastLog: null } as const;

    if (d.agent.last_log) {
      const lastLog = new Date(d.agent.last_log);
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      if (lastLog < threeMonthsAgo) {
        return { state: 'warning', messageKey: 'app.device.agent.out_of_date', lastLog: d.agent.lastLog } as const;
      }
    }

    return { state: 'active', messageKey: 'app.device.agent.compliant', lastLog: d.agent.lastLog } as const;
  });

  lockCredentials(): void {
    this._confirmAndExecute(
      ACTION_KEYS.lock_credentials,
      'primary',
      () => this._showDeviceFacade.rotateLocalCredentials(),
      'app.api.success.devices.rotate_local_credentials',
    );
  }

  autopilotReset(): void {
    this._confirmAndExecute(
      ACTION_KEYS.autopilot_reset,
      'warn',
      () => this._showDeviceFacade.autopilotReset(),
      'app.api.success.devices.autopilot_reset',
    );
  }

  retrieveBitlocker(): void {
    this._msg
      .showConfirm(
        this._translate.instant(`${ACTION_KEYS.retrieve_bitlocker}.title`),
        this._translate.instant(`${ACTION_KEYS.retrieve_bitlocker}.message`),
        '',
        '',
        'primary',
      )
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this._showDeviceFacade.retrieveBitlocker()),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: (res: any) => {
          this._msg.showSnackBar(ACTION_KEYS.bitlocker_keys);
          const keys = Array.isArray(res?.data) ? res.data : res?.data ? [res.data] : [];
          if (keys.length) {
            this._openBitlockerKeysDialog(keys);
          }
        },
      });
  }

  private _openBitlockerKeysDialog(keys: BitlockerKeyItem[]): void {
    this._dialog.open(BitlockerKeysDialogComponent, {
      data: { title: this._translate.instant(`${ACTION_KEYS.retrieve_bitlocker}.title`), keys },
      panelClass: ['av-dialog', 'av-dialog--full-content'],
    });
  }

  wipe(): void {
    this._openActionDialogAndExecute<WipePayload>(
      'wipe',
      ACTION_KEYS.wipe,
      (payload) => this._showDeviceFacade.wipe(payload),
      'app.api.success.devices.wipe',
    );
  }

  freshStart(): void {
    this._openActionDialogAndExecute<FreshStartPayload>(
      'freshStart',
      ACTION_KEYS.fresh_start,
      (payload) => this._showDeviceFacade.freshStart(payload),
      'app.api.success.devices.fresh_start',
    );
  }

  private _confirmAndExecute(
    actionKey: string,
    buttonColor: string,
    action: () => Observable<unknown>,
    successMsg: string,
  ): void {
    this._msg
      .showConfirm(
        this._translate.instant(`${actionKey}.title`),
        this._translate.instant(`${actionKey}.message`),
        '',
        '',
        buttonColor,
      )
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => action()),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: () => this._msg.showSnackBar(successMsg),
      });
  }

  private _openActionDialogAndExecute<T>(
    action: DeviceActionDialogAction,
    actionKey: string,
    facadeCall: (payload: T) => Observable<unknown>,
    successMsg: string,
  ): void {
    this._dialog
      .open<DeviceActionDialogComponent, DeviceActionDialogData, T>(DeviceActionDialogComponent, {
        data: {
          action,
          title: this._translate.instant(`${actionKey}.title`),
          message: this._translate.instant(`${actionKey}.message`),
        },
        panelClass: 'av-dialog',
      })
      .afterClosed()
      .pipe(
        filter((payload): payload is T => payload !== undefined),
        switchMap((payload) => facadeCall(payload)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: () => this._msg.showSnackBar(successMsg),
      });
  }
}
